import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom'
import {
  Home,
  Users,
  UserCog,
  Calendar,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  HelpCircle,
  Crown,
  ClipboardList,
  Building2,
  CalendarOff,
  BadgeCheck,
  Receipt,
  Scale,
  AlertTriangle,
  Heart,
  ShieldCheck,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase/config'
import { useAuth, type UserRole } from '@/contexts/AuthContext'
import type { AppPermission } from '@/lib/auth/permissions'
import { hasPermission } from '@/lib/auth/permissions'
import { getDisplayRoleLabel } from '@/lib/auth/display'
import { Avatar, Badge } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import NotificationBell from '@/components/shared/NotificationBell'
import RouteErrorBoundary from '@/components/shared/RouteErrorBoundary'
import TrialBanner from '@/components/shared/TrialBanner'
import Logo from '@/components/shared/Logo'
import { SyncStatusIndicator } from '@/components/offline'
import ThemeCycleToggle from '@/components/theme/ThemeCycleToggle'
import { useAgencyProfile } from '@/hooks/useAgencyProfile'
import { useMessageThreads } from '@/hooks/useFirestore'
import { getCaregiverPositionBadge, inferCaregiverPositionFromCertifications } from '@/lib/constants/careOptions'
import { toDate } from '@/lib/firebase/firestore'
import { BILLING_ENABLED, EVV_ENABLED } from '@/lib/featureFlags'
import type { MessageThread } from '@/types'

// Navigation item type
type NavItem = {
  name: string
  href: string
  icon: typeof Home
  roles?: UserRole[] // Legacy role-based visibility
  permissions?: AppPermission[] // Capability-based visibility
}

// Section-based navigation with divider labels (like AsiliGuide)
type NavSection = {
  label: string
  roles?: UserRole[] // Legacy role-based visibility
  permissions?: AppPermission[] // Capability-based visibility
  items: NavItem[]
}

// Helper function to check if user has access to a nav item
const hasAccess = (
  user: ReturnType<typeof useAuth>['user'],
  allowedRoles?: UserRole[],
  allowedPermissions?: AppPermission[]
): boolean => {
  if (!user) return false
  const hasRoleAccess = !allowedRoles || allowedRoles.includes(user.role)
  const hasPermissionAccess = !allowedPermissions || allowedPermissions.some(permission => hasPermission(user, permission))

  return hasRoleAccess && hasPermissionAccess
}

// ===========================================
// NAVIGATION CONFIGURATION BY ROLE
// ===========================================
// Organized into sections with divider labels:
//
// Admin/Supervisor:
//   OVERVIEW: Dashboard
//   MANAGEMENT: Clients, Caregivers
//   OPERATIONS: Schedule, Records, Authorizations, Billing, EVV
//
// Caregiver:
//   OVERVIEW: My Dashboard
//   MY WORK: My Schedule
//
// Billing/QA:
//   OVERVIEW: Dashboard
//   MANAGEMENT: Clients
//   OPERATIONS: Authorizations, Billing, EVV, Messages
// ===========================================

// Section-level `roles` is intentionally OMITTED. Section visibility is
// driven entirely by which items inside are permitted to the current user;
// the trailing `.filter(section => section.items.length > 0)` collapses any
// section that ends up empty. This is what lets a caregiver who's been
// granted `billing.read` see the BILLING section without us having to
// duplicate role lists at every level.
const navigationSections: NavSection[] = [
  {
    label: 'OVERVIEW',
    items: [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: Home,
        permissions: ['dashboard.read'],
      },
      {
        name: 'My Dashboard',
        href: '/my-dashboard',
        icon: Home,
        roles: ['caregiver'],
      },
    ],
  },
  {
    label: 'MY WORK',
    items: [
      {
        name: 'My Schedule',
        href: '/my-schedule',
        icon: Calendar,
        roles: ['caregiver'],
      },
      {
        name: 'Request Time Off',
        href: '/request-time-off',
        icon: CalendarOff,
        // Caregiver-mode UX surface — admins/supervisors who happen to have
        // `time_off.request` in their role defaults still shouldn't see this
        // here; they manage time off via the staff-side OPERATIONS > Time Off.
        roles: ['caregiver'],
      },
      // Messages intentionally not duplicated here — it lives in OPERATIONS
      // gated by `messages.read`, which is in every role's defaults including
      // caregiver. Keeping it in one place avoids the same item rendering twice.
    ],
  },
  {
    label: 'MANAGEMENT',
    items: [
      {
        name: 'Clients',
        href: '/clients',
        icon: Users,
        permissions: ['clients.read'],
      },
      {
        name: 'Caregivers',
        href: '/caregivers',
        icon: UserCog,
        permissions: ['caregivers.read'],
      },
      {
        name: 'Family Feedback',
        href: '/family-feedback',
        icon: Heart,
        permissions: ['family_feedback.manage'],
      },
    ],
  },
  {
    label: 'OPERATIONS',
    // Day-of work, workforce admin, and inter-staff comms.
    items: [
      {
        name: 'Schedule',
        href: '/schedule',
        icon: Calendar,
        permissions: ['schedule.read'],
      },
      ...(EVV_ENABLED
        ? [{
            name: 'EVV',
            href: '/evv',
            icon: ShieldCheck,
            permissions: ['evv.read'] as AppPermission[],
          }]
        : []),
      {
        name: 'Time Off',
        href: '/time-off',
        icon: CalendarOff,
        roles: ['admin', 'supervisor'],
        permissions: ['time_off.manage'],
      },
      {
        name: 'Messages',
        href: '/messages',
        icon: MessageSquare,
        permissions: ['messages.read'],
      },
    ],
  },
  {
    label: 'DOCUMENTATION',
    // Clinical paperwork — visit notes, assessments, care plans, authorizations.
    items: [
      {
        name: 'Notes Review',
        href: '/records',
        icon: ClipboardList,
        permissions: ['records.read'],
      },
      {
        name: 'Incidents',
        href: '/incidents',
        icon: AlertTriangle,
        permissions: ['incidents.read'],
      },
      {
        name: 'Forms',
        href: '/forms',
        icon: FileText,
        permissions: ['forms.read'],
      },
      {
        name: 'Authorizations',
        href: '/authorizations',
        icon: BadgeCheck,
        permissions: ['authorizations.read'],
      },
    ],
  },
  ...(BILLING_ENABLED
    ? ([{
        label: 'BILLING',
        // Revenue cycle: claims, ERAs, payer enrollments, denial appeals.
        items: [
          {
            name: 'Billing',
            href: '/billing',
            icon: Receipt,
            permissions: ['billing.read'],
          },
          {
            name: 'Eligibility',
            href: '/eligibility',
            icon: ShieldCheck,
            permissions: ['billing.read'],
          },
          {
            name: 'Appeals',
            href: '/billing/appeals',
            icon: Scale,
            permissions: ['billing.read'],
          },
          {
            name: 'Payer Enrollments',
            href: '/billing/enrollments',
            icon: Building2,
            permissions: ['billing.read'],
          },
        ],
      }] as NavSection[])
    : []),
]

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [bottomActionsCollapsed, setBottomActionsCollapsed] = useState(true)
  const bottomActionsRef = useRef<HTMLDivElement | null>(null)
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { agency } = useAgencyProfile(user?.agencyId)
  const { data: messageThreadsData } = useMessageThreads()

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const [pendingNotesCount, setPendingNotesCount] = useState(0)
  const [openIncidentsCount, setOpenIncidentsCount] = useState(0)
  const [pendingTimeOffCount, setPendingTimeOffCount] = useState(0)
  const [openScheduleCount, setOpenScheduleCount] = useState(0)
  const [caregiverTimeOffAlertCount, setCaregiverTimeOffAlertCount] = useState(0)
  const [caregiverPendingScheduleCount, setCaregiverPendingScheduleCount] = useState(0)
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false)
  const [pendingFamilyFeedbackCount, setPendingFamilyFeedbackCount] = useState(0)
  const [pendingFormsCount, setPendingFormsCount] = useState(0)
  const [authorizationAlertCount, setAuthorizationAlertCount] = useState(0)

  const fullName = user ? `${user.firstName} ${user.lastName}` : ''
  const userRole = user?.role
  const hasSettingsAccess = hasPermission(user, 'settings.manage')
  const messageThreads = Array.isArray(messageThreadsData)
    ? (messageThreadsData as MessageThread[])
    : []
  const unreadMessageCount = messageThreads.filter(
    thread => Array.isArray(thread?.unreadBy) && thread.unreadBy.includes(user?.uid || '')
  ).length
  const caregiverTimeOffSeenKey = user?.uid ? `caregiver-time-off-last-seen:${user.uid}` : null
  const agencyName = typeof agency?.name === 'string' && agency.name.trim()
    ? agency.name
    : (user?.agencyName || 'AsiliFlow')
  const userRoleLabel = getDisplayRoleLabel(user, agency)
  const supportEmail = typeof agency?.email === 'string' && agency.email.trim()
    ? agency.email.trim()
    : 'support@getasili.com'
  const userCertifications = Array.isArray(user?.certifications) ? user.certifications : []
  const inferredCaregiverPosition = inferCaregiverPositionFromCertifications(userCertifications)
  const customPosition = typeof user?.customPosition === 'string' ? user.customPosition.trim() : ''
  const positionBadge = userRole === 'caregiver'
    ? getCaregiverPositionBadge(user?.position || inferredCaregiverPosition, customPosition)
    : customPosition
  const trialEnd = toDate(agency?.subscription?.trialEndsAt)
  const trialDaysLeft = trialEnd
    ? Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null
  const showTrialBannerOffset = Boolean(
    BILLING_ENABLED &&
    !trialBannerDismissed &&
    user &&
    ['admin', 'supervisor'].includes(user.role) &&
    agency?.subscription?.status === 'trial' &&
    trialDaysLeft !== null &&
    trialDaysLeft <= 14 &&
    trialDaysLeft > 0
  )

  // Live listener for shift notes that need staff follow-up.
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'records.read')) {
      setPendingNotesCount(0)
      return
    }
    const q = query(
      collection(db, 'visitNotes'),
      where('agencyId', '==', user.agencyId),
      where('status', 'in', ['submitted', 'revision_requested']),
      orderBy('createdAt', 'desc'),
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingNotesCount(snapshot.size)
    }, () => { /* non-critical */ })
    return unsubscribe
  }, [user?.agencyId, user?.permissions, user?.role])

  // Live listener for authorizations that are expired or expiring within 60 days
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'authorizations.read')) {
      setAuthorizationAlertCount(0)
      return
    }

    const q = query(
      collection(db, 'authorizations'),
      where('agencyId', '==', user.agencyId),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now()
      const cutoff = now + 60 * 24 * 60 * 60 * 1000
      let count = 0

      snapshot.docs.forEach((doc) => {
        const data = doc.data() as {
          status?: string
          endDate?: { toDate?: () => Date } | string | Date
        }
        const endDate = toDate(data.endDate)
        const endTime = endDate?.getTime()
        const isExpired = data.status === 'expired' || (typeof endTime === 'number' && endTime <= now)
        const isExpiringWithin60Days =
          data.status === 'active' &&
          typeof endTime === 'number' &&
          endTime > now &&
          endTime <= cutoff

        if (isExpired || isExpiringWithin60Days) {
          count++
        }
      })

      setAuthorizationAlertCount(count)
    }, () => {
      setAuthorizationAlertCount(0)
    })

    return unsubscribe
  }, [user?.agencyId, user?.permissions, user?.role])

  // Live listener for new reported incidents count
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'incidents.read')) {
      setOpenIncidentsCount(0)
      return
    }
    const q = query(
      collection(db, 'incidents'),
      where('agencyId', '==', user.agencyId),
      where('status', '==', 'reported'),
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOpenIncidentsCount(snapshot.size)
    }, () => { /* non-critical */ })
    return unsubscribe
  }, [user?.agencyId])

  // Live listener for forms pending signature (awaiting client or admin counter-sig)
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'forms.read')) {
      setPendingFormsCount(0)
      return
    }
    const q = query(
      collection(db, 'plansOfCare'),
      where('agencyId', '==', user.agencyId),
      where('signingStatus', 'in', ['pending_client', 'pending_admin']),
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingFormsCount(snapshot.size)
    }, () => { /* non-critical */ })
    return unsubscribe
  }, [user?.agencyId])

  // Live listener for pending time off requests
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'time_off.manage')) {
      setPendingTimeOffCount(0)
      return
    }
    const q = query(
      collection(db, 'timeOffRequests'),
      where('agencyId', '==', user.agencyId),
      where('status', '==', 'pending'),
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingTimeOffCount(snapshot.size)
    }, () => { /* non-critical */ })
    return unsubscribe
  }, [user?.agencyId])

  // Live listener for open shifts that need scheduling coverage.
  // Mirrors the client-side filter in `useOpenShifts` — past unfilled shifts
  // are operational debt and shouldn't inflate the actionable badge count.
  useEffect(() => {
    if (!user?.agencyId || !hasPermission(user, 'schedule.read')) {
      setOpenScheduleCount(0)
      return
    }
    const q = query(
      collection(db, 'visits'),
      where('agencyId', '==', user.agencyId),
      where('status', '==', 'open'),
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now()
      let actionable = 0
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as { scheduledStart?: { toDate?: () => Date } | string | Date }
        const start = (data.scheduledStart as { toDate?: () => Date })?.toDate?.()
          ?? (data.scheduledStart instanceof Date ? data.scheduledStart : null)
          ?? (typeof data.scheduledStart === 'string' ? new Date(data.scheduledStart) : null)
        if (!start) return
        if (start.getTime() >= now) actionable++
      })
      setOpenScheduleCount(actionable)
    }, () => { /* non-critical */ })
    return unsubscribe
  }, [user?.agencyId])

  // Live listener for newly submitted family feedback
  useEffect(() => {
    if (!user?.agencyId || !hasSettingsAccess) {
      setPendingFamilyFeedbackCount(0)
      return
    }

    const q = query(
      collection(db, 'familyFeedback'),
      where('agencyId', '==', user.agencyId),
      where('status', '==', 'submitted'),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingFamilyFeedbackCount(snapshot.size)
    }, () => {
      setPendingFamilyFeedbackCount(0)
    })

    return unsubscribe
  }, [hasSettingsAccess, user?.agencyId])

  useEffect(() => {
    if (userRole !== 'caregiver' || !caregiverTimeOffSeenKey || location.pathname !== '/request-time-off') {
      return
    }

    const now = Date.now()
    try {
      localStorage.setItem(caregiverTimeOffSeenKey, String(now))
    } catch {
      // Ignore localStorage write failures
    }
    setCaregiverTimeOffAlertCount(0)
  }, [caregiverTimeOffSeenKey, location.pathname, userRole])

  useEffect(() => {
    if (userRole !== 'caregiver' || !user?.uid || !user.agencyId || !caregiverTimeOffSeenKey) {
      setCaregiverTimeOffAlertCount(0)
      return
    }

    const q = query(
      collection(db, 'timeOffRequests'),
      where('agencyId', '==', user.agencyId),
      where('caregiverId', '==', user.uid),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => doc.data() as {
        status?: 'pending' | 'approved' | 'denied' | 'cancelled'
        reviewedAt?: unknown
        updatedAt?: unknown
      })

      const pendingCount = requests.filter(request => request.status === 'pending').length

      let lastSeenAt = 0
      try {
        const stored = localStorage.getItem(caregiverTimeOffSeenKey)
        lastSeenAt = stored ? Number(stored) || 0 : 0
      } catch {
        // Ignore localStorage read failures
      }

      const reviewedCount = location.pathname === '/request-time-off'
        ? 0
        : requests.filter((request) => {
            if (request.status === 'pending' || request.status === 'cancelled') return false

            const reviewedAt = request.reviewedAt as { seconds?: number } | undefined
            const updatedAt = request.updatedAt as { seconds?: number } | undefined
            const eventTime = Math.max(
              (reviewedAt?.seconds || 0) * 1000,
              (updatedAt?.seconds || 0) * 1000,
            )

            return eventTime > lastSeenAt
          }).length

      setCaregiverTimeOffAlertCount(pendingCount + reviewedCount)
    }, () => {
      setCaregiverTimeOffAlertCount(0)
    })

    return unsubscribe
  }, [caregiverTimeOffSeenKey, location.pathname, user?.agencyId, user?.uid, userRole])

  useEffect(() => {
    if (userRole !== 'caregiver' || !user?.agencyId || (!user?.uid && !user?.id)) {
      setCaregiverPendingScheduleCount(0)
      return
    }

    const caregiverIds = Array.from(new Set([user.id, user.uid].filter(Boolean) as string[]))
    const visitDocs = new Map<string, { status?: string; isDeleted?: boolean }>()

    const updateCount = () => {
      let count = 0
      visitDocs.forEach((visit) => {
        if (visit.status === 'pending_confirmation' && visit.isDeleted !== true) {
          count++
        }
      })
      setCaregiverPendingScheduleCount(count)
    }

    const unsubscribes = caregiverIds.map((caregiverId) => {
      const q = query(
        collection(db, 'visits'),
        where('agencyId', '==', user.agencyId),
        where('caregiverId', '==', caregiverId),
        orderBy('scheduledStart', 'asc'),
      )

      return onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            visitDocs.delete(change.doc.id)
            return
          }

          visitDocs.set(change.doc.id, change.doc.data() as { status?: string; isDeleted?: boolean })
        })
        updateCount()
      }, () => {
        setCaregiverPendingScheduleCount(0)
      })
    })

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe())
    }
  }, [user?.agencyId, user?.id, user?.uid, userRole])

  useEffect(() => {
    if (bottomActionsCollapsed) return

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (bottomActionsRef.current?.contains(target)) return

      setBottomActionsCollapsed(true)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [bottomActionsCollapsed])

  useEffect(() => {
    if (!sidebarOpen) return

    const previousOverflow = document.body.style.overflow
    const previousTouchAction = document.body.style.touchAction
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'

    return () => {
      document.body.style.overflow = previousOverflow
      document.body.style.touchAction = previousTouchAction
    }
  }, [sidebarOpen])

  // Filter navigation sections and items based on user role
  const filteredSections = navigationSections
    .filter(section => hasAccess(user, section.roles, section.permissions))
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasAccess(user, item.roles, item.permissions)),
    }))
    .filter(section => section.items.length > 0)
  const getNavBadgeCount = (itemName: string) => {
    if (itemName === 'Schedule') return openScheduleCount
    if (itemName === 'Records') return pendingNotesCount + openIncidentsCount
    if (itemName === 'Notes Review') return pendingNotesCount
    if (itemName === 'Incidents') return openIncidentsCount
    if (itemName === 'Time Off') return pendingTimeOffCount
    if (itemName === 'Request Time Off') return caregiverTimeOffAlertCount
    if (itemName === 'My Schedule') return caregiverPendingScheduleCount
    if (itemName === 'Family Feedback') return pendingFamilyFeedbackCount
    if (itemName === 'Messages') return unreadMessageCount
    if (itemName === 'Forms') return pendingFormsCount
    if (itemName === 'Authorizations') return authorizationAlertCount
    return 0
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Trial expiration banner */}
      <TrialBanner onDismissChange={setTrialBannerDismissed} />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 z-50 flex w-64 transform flex-col overscroll-contain border-r border-gray-200 bg-white transition-transform duration-200 ease-in-out dark:border-gray-800 dark:bg-gray-900 lg:translate-x-0',
          showTrialBannerOffset ? 'top-12 bottom-0' : 'inset-y-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-6 dark:border-gray-800">
          <Logo size="md" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Info Section */}
        <div className="px-4 py-4">
          {/* User Avatar and Name - Clickable to go to profile */}
          <button
            onClick={() => {
              navigate('/profile')
              setSidebarOpen(false)
            }}
            className="mb-4 flex w-full items-center gap-3 rounded-lg p-2 -m-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Avatar name={fullName} size="md" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{fullName}</p>
              <p className="mt-0.5 text-xs capitalize text-gray-500 dark:text-gray-400">{userRoleLabel}</p>
            </div>
          </button>
          {/* Agency Name */}
          <div className="w-full truncate rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            {agencyName}
          </div>
          {/* Role Badge */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 border border-yellow-200 rounded-full text-xs font-medium">
              <Crown className="h-3 w-3" />
              <span className="capitalize">{userRoleLabel}</span>
            </div>
            {positionBadge && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full border border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 text-xs font-medium">
                <span>{positionBadge}</span>
              </div>
            )}
          </div>
        </div>

        {/* Separator */}
        <div className="mx-4 border-b border-gray-200 dark:border-gray-800" />

        {/* Navigation */}
        <nav className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-3 pb-6 pt-4">
          {(() => {
            // An item is a "parent" of another if its href is a strict prefix.
            // Parents must use NavLink's `end` so they don't highlight on child routes.
            const allHrefs = filteredSections.flatMap(s => s.items.map(i => i.href))
            const parentHrefs = new Set(
              allHrefs.filter(h => allHrefs.some(o => o !== h && o.startsWith(h + '/')))
            )
            return filteredSections.map((section) => (
              <div key={section.label}>
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {section.label}
                </p>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const badgeCount = getNavBadgeCount(item.name)

                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        end={parentHrefs.has(item.href)}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-medium transition-colors',
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                          )
                        }
                      >
                        <item.icon className="h-[18px] w-[18px] shrink-0" />
                        <span className="flex-1 truncate text-left">{item.name}</span>
                        {badgeCount > 0 && (
                          <Badge variant="warning" className="ml-auto text-xs px-1.5">{badgeCount}</Badge>
                        )}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            ))
          })()}
        </nav>

        {/* Bottom Section - Settings, Support & Logout */}
        <div ref={bottomActionsRef} className="overscroll-contain border-t border-gray-200 px-4 py-2 dark:border-gray-800">
          <div>
            <button
              type="button"
              onClick={() => setBottomActionsCollapsed(prev => !prev)}
              aria-expanded={!bottomActionsCollapsed}
              aria-controls="sidebar-bottom-actions"
              title={bottomActionsCollapsed ? 'Expand actions' : 'Collapse actions'}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              <span>Quick actions</span>
              {bottomActionsCollapsed ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          </div>

          <div
            id="sidebar-bottom-actions"
            className={cn(
              'space-y-1 overscroll-contain transition-all duration-200 ease-in-out',
              bottomActionsCollapsed
                ? 'max-h-0 overflow-hidden opacity-0 pointer-events-none'
                : 'max-h-48 overflow-y-auto opacity-100'
            )}
          >
            {/* Settings - admin/supervisor go to /settings, caregivers go to /profile */}
            <div className="flex items-center gap-2">
              <NavLink
                to={hasSettingsAccess ? '/settings' : '/profile'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                  )
                }
              >
                <Settings className="h-5 w-5 shrink-0" />
                Settings
              </NavLink>
              <ThemeCycleToggle />
            </div>
            <button
              onClick={() => window.open(`mailto:${supportEmail}`, '_blank')}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              <HelpCircle className="h-5 w-5 shrink-0" />
              Support
            </button>
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/80 backdrop-blur-lg dark:border-gray-800 dark:bg-gray-900/80">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Search - placeholder */}
            <div className="flex-1 max-w-md mx-4 lg:mx-0">
              {/* Search input would go here */}
            </div>

            {/* Right side - Messages & Notifications */}
            <div className="flex items-center gap-2">
              {userRole === 'caregiver' && <ThemeCycleToggle />}
              <Link
                to="/messages"
                className="relative rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                title="Messages"
              >
                <MessageSquare className="h-5 w-5" />
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </Link>
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>

      {/* Offline Sync Status Indicator */}
      <SyncStatusIndicator />
    </div>
  )
}
